import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { GoodsDto, GoodsStatus, OrderGoodsItemDto, SiteType } from '../../api/ClientApi';
import { RootState } from '../../redux/reducers';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import { importArticles } from '../../redux/actions/articleActions';
import { Box, Chip, IconButton, Paper, TablePagination } from '@mui/material';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { StyledTableCell } from '../../common/components/styled/StyledTable';
import { DimensionTypeDimensionSR } from '../../common/Types';
import { TableDetailsType, createGoodsData, currGoodsTableRows } from './GoodsHelper';
import BatchForm from './BatchForm';
import { formatNumber } from '../../common/Extensions';
import { isGoodsCollapsed } from '../../redux/selectors/collapsibleSelector';
import { clearGoodsCollapsible, toggleGoodsIdCollapsible } from '../../redux/actions/collapsibleOpenStateActions';
import GoodsTableMenu from './GoodsTableMenu';
import WriteOffGoodsForm from './GoodsStatusManipulationForm/WriteOffGoodsForm';
import UseGoodsForm from './GoodsStatusManipulationForm/UseGoodsForm';
import SellGoodsForm from './GoodsStatusManipulationForm/SellGoodsForm';
import { importClients } from '../../redux/actions/clientActions';
import GoodsTableCollapsibleArrivalInfo from './GoodsTableCollapsibleArrivalInfo';
import {
    canInstallGoods,
    canManipulateGoods,
    isEmployeeFinanceManager,
    isEmployeeWarehouseOwnerOnly,
} from '../../redux/selectors/employeeSelector';
import GoodsTableCollapsibleBasicInfo from './GoodsTableCollapsibleBasicInfo';
import GoodsTableCollapsibleExitInfo from './GoodsTableCollapsibleExitInfo';

import {
    MobileCardRow,
    mobileHiddenHeadSx,
    mobileTableSx,
    useIsMobile,
} from '../../common/components/mobile/MobilePrimitives';

type GoodsRow = ReturnType<typeof createGoodsData>;

type GoodsRowProps = {
    row: GoodsRow;
    isSiteTable: boolean;
    isSiteWithClient: boolean;
    tableDetailsType: TableDetailsType | null;
    goodsStatusFilter: GoodsStatus;
    disableStatusFilter?: boolean;
    isFinanceManager: boolean;
    isHighestRoleWarehouseOwner: boolean;
    canChangeGoods: boolean;
    canInstall: boolean;
    onOpenBatchForm: (batchId?: number, manufacturer?: string, title?: string, innerCode?: string) => void;
    onSellGoods: (articleId: number, inStockQuantity: number) => void;
    onUseGoods: (articleId: number, inStockQuantity: number) => void;
    onWriteOffGoods: (articleId: number, inStockQuantity: number) => void;
};

/**
 * One article row: desktop table row + collapsible details, or a mobile card.
 * Lives at module scope so React updates rows in place instead of remounting them on every parent render.
 */
function Row({
    row,
    isSiteTable,
    isSiteWithClient,
    tableDetailsType,
    goodsStatusFilter,
    disableStatusFilter,
    isFinanceManager,
    isHighestRoleWarehouseOwner,
    canChangeGoods,
    canInstall,
    onOpenBatchForm,
    onSellGoods,
    onUseGoods,
    onWriteOffGoods,
}: GoodsRowProps) {
    const dispatch = useAppDispatch();

    const isCollapsed = useAppSelector(isGoodsCollapsed(row.articleId));

    const handleToggleOrderCollapsible = () => {
        dispatch(toggleGoodsIdCollapsible(row.articleId));
    };

    const handleBatchFormDataFill = (batchId?: number) => {
        onOpenBatchForm(batchId, row.manufacturer, row.title, row.innerCode);
    };

    const quantityCellContent = () => {
        if (disableStatusFilter || goodsStatusFilter !== GoodsStatus.InStock) {
            return (
                <>
                    {row.totalQuantity !== undefined && formatNumber(row.totalQuantity)}{' '}
                    {row.dimensionType ? DimensionTypeDimensionSR[row.dimensionType] : 'kom.'}
                </>
            );
        }
        return (
            <>
                {row.inStockQuantity !== undefined ? formatNumber(row.inStockQuantity) : '/'}{' '}
                {row.totalQuantity !== undefined && (
                    <span style={{ color: '#aaa' }}>/ {formatNumber(row.totalQuantity)}</span>
                )}{' '}
                {row.dimensionType ? DimensionTypeDimensionSR[row.dimensionType] : 'kom.'}
            </>
        );
    };

    const isMobile = useIsMobile();

    const goodsMenu =
        isSiteTable && canChangeGoods && goodsStatusFilter === GoodsStatus.InStock ? (
            <GoodsTableMenu
                articleId={row.articleId ?? 0}
                sellGoods={(articleId: number) => onSellGoods(articleId, row.inStockQuantity ?? 0)}
                useGoods={(articleId: number) => onUseGoods(articleId, row.inStockQuantity ?? 0)}
                writeOffGoods={(articleId: number) => onWriteOffGoods(articleId, row.inStockQuantity ?? 0)}
                writeOffGoodsForTransport={(goodsId: number) => console.log(goodsId)}
                isSiteWithClient={isSiteWithClient}
                canInstall={canInstall}
            />
        ) : null;

    const detailsContent = (
        <>
            {isHighestRoleWarehouseOwner ? (
                tableDetailsType === TableDetailsType.Basic ? (
                    <GoodsTableCollapsibleBasicInfo
                        isCollapsed={isCollapsed}
                        dimensionType={row.dimensionType}
                        goodsBasicInfo={row.goodsBasicInfo}
                        isSiteTable={isSiteTable}
                    />
                ) : tableDetailsType === TableDetailsType.Exit ? (
                    <GoodsTableCollapsibleExitInfo
                        isCollapsed={isCollapsed}
                        dimensionType={row.dimensionType}
                        goodsExitInfo={row.goodsExitInfo}
                        isSiteTable={isSiteTable}
                    />
                ) : (
                    <></>
                )
            ) : tableDetailsType === TableDetailsType.Basic ? (
                <GoodsTableCollapsibleBasicInfo
                    isCollapsed={isCollapsed}
                    dimensionType={row.dimensionType}
                    goodsBasicInfo={row.goodsBasicInfo}
                    isSiteTable={isSiteTable}
                />
            ) : tableDetailsType === TableDetailsType.Exit ? (
                <GoodsTableCollapsibleExitInfo
                    isCollapsed={isCollapsed}
                    dimensionType={row.dimensionType}
                    goodsExitInfo={row.goodsExitInfo}
                    isSiteTable={isSiteTable}
                />
            ) : tableDetailsType === TableDetailsType.Arrival ? (
                <GoodsTableCollapsibleArrivalInfo
                    dimensionType={row.dimensionType}
                    goodsArrivalInfo={row.goodsArrivalInfo}
                    handleOpenBatchForm={handleBatchFormDataFill}
                    isCollapsed={isCollapsed}
                    isSiteTable={isSiteTable}
                />
            ) : (
                <></>
            )}
        </>
    );

    if (isMobile) {
        return (
            <>
                <MobileCardRow
                    title={row.title ?? '/'}
                    subtitle={[row.manufacturer, row.innerCode].filter((v) => !!v).join(' · ')}
                    highlighted={!isCollapsed}
                    header={
                        !!row.labels && row.labels.length > 0
                            ? row.labels.map((l) => (
                                  <Chip key={l} size="small" color="primary" variant="outlined" label={l} />
                              ))
                            : undefined
                    }
                    actions={
                        <Box sx={{ display: 'flex' }}>
                            <IconButton aria-label="expand row" size="small" onClick={handleToggleOrderCollapsible}>
                                {!isCollapsed ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                            </IconButton>
                            {goodsMenu}
                        </Box>
                    }
                    fields={[
                        { label: 'Količina', value: quantityCellContent() },
                        { label: 'Šifra proizv.', value: row.outterCode ?? '/' },
                        ...(isFinanceManager
                            ? [{ label: 'Cena', value: row.averagePrice ? formatNumber(row.averagePrice) : '/' }]
                            : []),
                    ]}
                />
                <TableRow>
                    <TableCell sx={{ p: 0, borderBottom: 'none' }} colSpan={99}>
                        {detailsContent}
                    </TableCell>
                </TableRow>
            </>
        );
    }

    return (
        <>
            <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 }, bgcolor: !isCollapsed ? '#eee' : '' }}>
                <TableCell>
                    <IconButton aria-label="expand row" size="small" onClick={() => handleToggleOrderCollapsible()}>
                        {!isCollapsed ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
                <TableCell align="center">{row.innerCode ?? '/'}</TableCell>
                <TableCell component="th" scope="row" align="center">
                    {row.title ?? '/'}
                </TableCell>
                <TableCell component="th" scope="row" align="center">
                    {row.manufacturer ?? '/'}
                </TableCell>
                <TableCell align="center">{row.outterCode ?? '/'}</TableCell>
                <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        {!!row.labels && row.labels.length > 0
                            ? row.labels.map((l) => {
                                  return <Chip key={l} color="primary" variant="outlined" label={l} />;
                              })
                            : '/'}
                    </Box>
                </TableCell>
                <TableCell align="center">{quantityCellContent()}</TableCell>
                {isFinanceManager && (
                    <TableCell align="center">{row.averagePrice ? formatNumber(row.averagePrice) : '/'}</TableCell>
                )}
                {isSiteTable && canChangeGoods && goodsStatusFilter === GoodsStatus.InStock && (
                    <TableCell align="center">{goodsMenu}</TableCell>
                )}
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                    {detailsContent}
                </TableCell>
            </TableRow>
        </>
    );
}

type GoodsTableProps = {
    isSiteTable: boolean;
    isSiteWithClient: boolean;
    tableDetailsType: TableDetailsType | null;
    goods: GoodsDto[] | null;
    approvedOrderGoodsItems: OrderGoodsItemDto[] | null;
    articleFilter: string;
    labelsFilter: string;
    situationFilter?: number;
    uniqueBatchIdFilter: string;
    batchSuppliersFilter: string;
    siteNameFilter: string;
    siteTypeFilter: SiteType | null;
    goodsStatusFilter: GoodsStatus;
    disableStatusFilter?: boolean;
};

export default function GoodsTable(props: GoodsTableProps) {
    const {
        isSiteTable,
        isSiteWithClient,
        tableDetailsType,
        goods,
        approvedOrderGoodsItems,
        articleFilter,
        labelsFilter,
        situationFilter,
        batchSuppliersFilter,
        uniqueBatchIdFilter,
        siteNameFilter,
        siteTypeFilter,
        goodsStatusFilter,
        disableStatusFilter,
    } = props;

    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(10);

    const [editBatchId, setEditBatchId] = React.useState<number | undefined>(undefined);
    const [currArticleInfo, setCurrArticleInfo] = React.useState<string | undefined>(undefined);

    const [writeOffArticleId, setWriteOffArticleId] = React.useState<number | undefined>(undefined);
    const [useArticleId, setUseArticleId] = React.useState<number | undefined>(undefined);
    const [sellArticleId, setSellArticleId] = React.useState<number | undefined>(undefined);
    const [goodsInStockQuantity, setGoodsInStockQuantity] = React.useState<number | undefined>(undefined);

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const isFinanceManager = useAppSelector(isEmployeeFinanceManager);
    const isHighestRoleWarehouseOwner = useAppSelector(isEmployeeWarehouseOwnerOnly);
    const canChangeGoods = useAppSelector(canManipulateGoods);
    const canInstall = useAppSelector(canInstallGoods);

    const articles = useAppSelector((state: RootState) => state.articles.articles);
    const clients = useAppSelector((state: RootState) => state.clients.clients);

    const companyDetails = useAppSelector((state) => state.companyDetails.companyDetails);

    const dispatch = useAppDispatch();

    React.useEffect(() => {
        if (!articles) {
            dispatch(importArticles());
        }
    }, [dispatch]);

    React.useEffect(() => {
        if (!clients) {
            dispatch(importClients());
        }
    }, [dispatch]);

    React.useEffect(() => {
        dispatch(clearGoodsCollapsible());
    }, [dispatch]);

    const handleOpenBatchForm = (id?: number, manufacturer?: string, title?: string, innerCode?: string) => {
        let artInfo = title ? (manufacturer ? title + ', ' + manufacturer : title) : (manufacturer ?? '');

        artInfo += innerCode ? ' (' + innerCode + ')' : '';

        setCurrArticleInfo(artInfo);
        setEditBatchId(id);
    };

    const handleCloseBatchForm = () => {
        setEditBatchId(undefined);
    };

    const handleOpenWriteOffGoodsForm = (id: number, inStockQuantity: number) => {
        setWriteOffArticleId(id);
        handleSetGoodsInStockQuantity(inStockQuantity);
    };

    const handleCloseWriteOffGoodsForm = () => {
        setWriteOffArticleId(undefined);
        handleClearGoodsInStockQuantity();
    };

    const handleOpenUseGoodsForm = (id: number, inStockQuantity: number) => {
        setUseArticleId(id);
        handleSetGoodsInStockQuantity(inStockQuantity);
    };

    const handleCloseUseGoodsForm = () => {
        setUseArticleId(undefined);
        handleClearGoodsInStockQuantity();
    };

    const handleOpenSellGoodsForm = (id: number, inStockQuantity: number) => {
        setSellArticleId(id);
        handleSetGoodsInStockQuantity(inStockQuantity);
    };

    const handleCloseSellGoodsForm = () => {
        setSellArticleId(undefined);
        handleClearGoodsInStockQuantity();
    };

    const handleSetGoodsInStockQuantity = (quantity: number) => {
        setGoodsInStockQuantity(quantity);
    };

    const handleClearGoodsInStockQuantity = () => {
        setGoodsInStockQuantity(undefined);
    };

    const rows = React.useMemo(() => {
        return currGoodsTableRows(
            !isSiteTable,
            tableDetailsType,
            goods,
            approvedOrderGoodsItems,
            articles,
            articleFilter,
            labelsFilter,
            situationFilter ?? 0,
            goodsStatusFilter,
            uniqueBatchIdFilter,
            batchSuppliersFilter,
            siteNameFilter,
            siteTypeFilter ?? undefined,
            undefined,
            undefined,
            disableStatusFilter,
        );
    }, [
        goods,
        approvedOrderGoodsItems,
        articles,
        isSiteTable,
        tableDetailsType,
        articleFilter,
        labelsFilter,
        situationFilter,
        goodsStatusFilter,
        uniqueBatchIdFilter,
        batchSuppliersFilter,
        siteNameFilter,
        siteTypeFilter,
        disableStatusFilter,
    ]);

    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - (rows?.length ?? 0)) : 0;

    const visibleRows = React.useMemo(
        () => (!rows ? [] : [...rows].slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)),
        [
            page,
            rowsPerPage,
            goods,
            approvedOrderGoodsItems,
            articles,
            isSiteTable,
            tableDetailsType,
            articleFilter,
            labelsFilter,
            situationFilter,
            goodsStatusFilter,
            uniqueBatchIdFilter,
            batchSuppliersFilter,
            siteNameFilter,
            siteTypeFilter,
        ],
    );

    React.useEffect(() => {
        if (!rows || rows?.length <= page * rowsPerPage) {
            setPage(0);
        }
    }, [rows, page]);

    return (
        <>
            <Box sx={{ width: '100%' }}>
                <Paper sx={{ width: '100%', mb: 2 }}>
                    <TableContainer sx={{ margin: 'auto' }}>
                        <Table sx={mobileTableSx(500)} size={'small'} stickyHeader aria-label="simple table">
                            <TableHead sx={mobileHiddenHeadSx}>
                                <TableRow>
                                    <StyledTableCell sx={{ width: 70 }} />
                                    <StyledTableCell align="center">Interni kod proizvoda</StyledTableCell>
                                    <StyledTableCell align="center">Naziv</StyledTableCell>
                                    <StyledTableCell align="center">Proizvođač</StyledTableCell>
                                    <StyledTableCell align="center">Šifra artikla proizvođača</StyledTableCell>
                                    <StyledTableCell align="center">Tag</StyledTableCell>
                                    <StyledTableCell align="center">Količina</StyledTableCell>
                                    {isFinanceManager && (
                                        <StyledTableCell align="center">
                                            Cena {companyDetails?.currency ? '(' + companyDetails.currency + ')' : ''}
                                        </StyledTableCell>
                                    )}
                                    {isSiteTable && canChangeGoods && goodsStatusFilter === GoodsStatus.InStock && (
                                        <StyledTableCell align="center">Akcije</StyledTableCell>
                                    )}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {visibleRows?.map((row, index) => (
                                    <Row
                                        key={row.articleId ?? row.innerCode ?? index}
                                        row={row}
                                        isSiteTable={isSiteTable}
                                        isSiteWithClient={isSiteWithClient}
                                        tableDetailsType={tableDetailsType}
                                        goodsStatusFilter={goodsStatusFilter}
                                        disableStatusFilter={disableStatusFilter}
                                        isFinanceManager={isFinanceManager}
                                        isHighestRoleWarehouseOwner={isHighestRoleWarehouseOwner}
                                        canChangeGoods={canChangeGoods}
                                        canInstall={canInstall}
                                        onOpenBatchForm={handleOpenBatchForm}
                                        onSellGoods={handleOpenSellGoodsForm}
                                        onUseGoods={handleOpenUseGoodsForm}
                                        onWriteOffGoods={handleOpenWriteOffGoodsForm}
                                    />
                                ))}
                                {emptyRows > 0 && (
                                    <TableRow
                                        style={{
                                            height: 40 * emptyRows,
                                        }}
                                    >
                                        <TableCell colSpan={isFinanceManager ? 7 : 6} />
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50]}
                        component="div"
                        count={rows?.length ?? 0}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Paper>
            </Box>
            <BatchForm
                closeForm={handleCloseBatchForm}
                open={!!editBatchId}
                batchId={editBatchId}
                articleInfo={currArticleInfo}
            />
            <WriteOffGoodsForm
                articleId={writeOffArticleId}
                closeForm={handleCloseWriteOffGoodsForm}
                open={!!writeOffArticleId}
                goodsQuantity={goodsInStockQuantity ?? 0}
            />
            <UseGoodsForm
                articleId={useArticleId}
                closeForm={handleCloseUseGoodsForm}
                open={!!useArticleId}
                goodsQuantity={goodsInStockQuantity ?? 0}
            />
            <SellGoodsForm
                articleId={sellArticleId}
                closeForm={handleCloseSellGoodsForm}
                open={!!sellArticleId}
                goodsQuantity={goodsInStockQuantity ?? 0}
            />
        </>
    );
}
